import { resolveMediaUrl } from '../../utils/helpers';
import React, { useState, useEffect } from 'react';
import { useTheme } from '../../contexts/ThemeContext';
import { X, QrCode, Users, CheckCircle, Clock, RefreshCw } from 'lucide-react';
import QRCode from 'react-qr-code';

const QRAttendanceModal = ({ isOpen, onClose, session, classData, students, onUpdateAttendance }) => {
  const { theme } = useTheme();
  const [checkedInStudents, setCheckedInStudents] = useState([]);
  const [sessionActive, setSessionActive] = useState(true);

  // Generate unique QR code data for this session
  const qrData = JSON.stringify({
    type: 'class_attendance',
    sessionId: session.id,
    classId: classData.id,
    academyId: classData.academyId,
    date: session.date,
    time: session.time,
    timestamp: Date.now()
  });

  // Prevent body scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  // Simulate real-time check-ins (in real app, use WebSocket or polling)
  useEffect(() => {
    if (!isOpen) return;

    // Simulate students checking in over time
    const interval = setInterval(() => {
      if (checkedInStudents.length < students.length) {
        const notCheckedIn = students.filter(
          s => !checkedInStudents.find(c => c.userId === s.userId)
        );
        if (notCheckedIn.length > 0 && Math.random() > 0.7) {
          const randomStudent = notCheckedIn[Math.floor(Math.random() * notCheckedIn.length)];
          handleStudentCheckIn(randomStudent);
        }
      }
    }, 3000);

    return () => clearInterval(interval);
  }, [isOpen, checkedInStudents, students]);

  if (!isOpen) return null;

  const handleStudentCheckIn = (student) => {
    const checkIn = {
      userId: student.userId,
      firstName: student.firstName,
      lastName: student.lastName,
      profileImage: student.profileImage,
      checkedInAt: new Date().toLocaleTimeString('en-US', {
        hour: '2-digit',
        minute: '2-digit'
      }),
      status: 'present'
    };

    setCheckedInStudents(prev => {
      // Prevent duplicates
      if (prev.find(c => c.userId === student.userId)) return prev;
      return [...prev, checkIn];
    });
  };

  const handleSaveAndClose = () => {
    // Convert check-ins to attendance format
    const attendanceData = students.reduce((acc, student) => {
      const checkIn = checkedInStudents.find(c => c.userId === student.userId);
      acc[student.userId] = {
        status: checkIn ? 'present' : 'unmarked',
        note: checkIn ? `Checked in at ${checkIn.checkedInAt}` : '',
        checkedInAt: checkIn ? checkIn.checkedInAt : null
      };
      return acc;
    }, {});

    onUpdateAttendance(session.id, attendanceData);
    onClose();
  };

  const notCheckedIn = students.filter(
    s => !checkedInStudents.find(c => c.userId === s.userId)
  );

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('en-US', {
      weekday: 'long',
      month: 'long',
      day: 'numeric',
      year: 'numeric'
    });
  };

  return (
    <div
      className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 overflow-y-auto"
      onClick={onClose}
    >
      <div
        className={`${theme.card} rounded-xl w-full max-w-5xl max-h-[90vh] flex flex-col my-auto`}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="p-6 border-b border-gray-200 flex justify-between items-start">
          <div className="flex-1">
            <h2 className={`text-2xl font-bold ${theme.text} mb-2 flex items-center gap-2`}>
              <QrCode size={28} />
              QR Code Check-In
            </h2>
            <p className={theme.textSecondary}>
              {formatDate(session.date)} at {session.time}
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <X size={24} />
          </button>
        </div>

        {/* Main Content */}
        <div className="flex-1 overflow-y-auto p-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Left: QR Code Section */}
            <div className="flex flex-col">
              <div className={`${theme.card} border-2 border-blue-500 rounded-xl p-6 text-center`}>
                <h3 className={`text-lg font-bold ${theme.text} mb-4`}>
                  Students: Scan to Check In
                </h3>

                {/* QR Code */}
                <div className="bg-white p-6 rounded-lg inline-block mb-4">
                  <QRCode
                    value={qrData}
                    size={256}
                    level="H"
                  />
                </div>

                {/* Instructions */}
                <div className="text-sm space-y-2">
                  <p className={`${theme.text} font-semibold mb-3`}>How to Check In:</p>
                  <div className="text-left space-y-2 bg-blue-50 dark:bg-blue-900/20 p-4 rounded-lg">
                    <p className={theme.textSecondary}>1. Open the app on your phone</p>
                    <p className={theme.textSecondary}>2. Tap the QR scanner icon</p>
                    <p className={theme.textSecondary}>3. Point camera at this QR code</p>
                    <p className={theme.textSecondary}>4. Wait for confirmation</p>
                  </div>
                </div>

                {/* Session Status */}
                <div className="mt-4 pt-4 border-t border-gray-200">
                  <div className="flex items-center justify-center gap-2 text-sm">
                    {sessionActive ? (
                      <>
                        <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
                        <span className="text-green-600 font-semibold">Session Active</span>
                      </>
                    ) : (
                      <>
                        <Clock size={16} className="text-gray-400" />
                        <span className={theme.textSecondary}>Session Ended</span>
                      </>
                    )}
                  </div>
                </div>
              </div>

              {/* Stats Cards */}
              <div className="grid grid-cols-2 gap-4 mt-4">
                <div className={`${theme.card} border ${theme.border} rounded-xl p-4 text-center`}>
                  <div className={`text-3xl font-bold text-green-600 mb-1`}>
                    {checkedInStudents.length}
                  </div>
                  <div className={`text-sm ${theme.textSecondary}`}>Checked In</div>
                </div>
                <div className={`${theme.card} border ${theme.border} rounded-xl p-4 text-center`}>
                  <div className={`text-3xl font-bold text-orange-600 mb-1`}>
                    {notCheckedIn.length}
                  </div>
                  <div className={`text-sm ${theme.textSecondary}`}>Waiting</div>
                </div>
              </div>
            </div>

            {/* Right: Live Check-ins */}
            <div className="flex flex-col">
              <h3 className={`text-lg font-bold ${theme.text} mb-4 flex items-center gap-2`}>
                <Users size={20} />
                Live Check-Ins
                {checkedInStudents.length > 0 && (
                  <span className="px-2 py-1 bg-green-100 text-green-700 rounded text-sm font-semibold">
                    {checkedInStudents.length}/{students.length}
                  </span>
                )}
              </h3>

              {/* Progress Bar */}
              <div className="mb-4">
                <div className="flex justify-between text-sm mb-2">
                  <span className={theme.textSecondary}>Attendance Progress</span>
                  <span className="font-semibold">
                    {Math.round((checkedInStudents.length / students.length) * 100)}%
                  </span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-3">
                  <div
                    className="bg-green-600 h-3 rounded-full transition-all duration-500"
                    style={{ width: `${(checkedInStudents.length / students.length) * 100}%` }}
                  />
                </div>
              </div>

              {/* Checked In Students List */}
              <div className="flex-1 overflow-y-auto space-y-2 min-h-[300px]">
                {checkedInStudents.length === 0 ? (
                  <div className="flex flex-col items-center justify-center h-full text-center py-12">
                    <RefreshCw size={48} className="text-gray-300 mb-3 animate-spin" />
                    <p className={`${theme.text} font-semibold mb-1`}>
                      Waiting for students...
                    </p>
                    <p className={`text-sm ${theme.textSecondary}`}>
                      Students will appear here as they scan the QR code
                    </p>
                  </div>
                ) : (
                  <>
                    {checkedInStudents.map((checkIn, index) => (
                      <div
                        key={checkIn.userId}
                        className={`${theme.card} border-2 border-green-500 rounded-lg p-3 flex items-center gap-3 animate-fade-in`}
                        style={{ animationDelay: `${index * 0.1}s` }}
                      >
                        <CheckCircle size={20} className="text-green-600" />
                        <img
                          src={resolveMediaUrl(checkIn.profileImage)}
                          alt={`${checkIn.firstName} ${checkIn.lastName}`}
                          className="w-10 h-10 rounded-full object-cover"
                        />
                        <div className="flex-1">
                          <h4 className={`font-semibold ${theme.text}`}>
                            {checkIn.firstName} {checkIn.lastName}
                          </h4>
                          <p className={`text-xs ${theme.textSecondary}`}>
                            Checked in at {checkIn.checkedInAt}
                          </p>
                        </div>
                      </div>
                    ))}
                  </>
                )}
              </div>

              {/* Waiting List */}
              {notCheckedIn.length > 0 && checkedInStudents.length > 0 && (
                <div className="mt-4 pt-4 border-t border-gray-200">
                  <h4 className={`text-sm font-semibold ${theme.text} mb-2`}>
                    Still Waiting ({notCheckedIn.length})
                  </h4>
                  <div className="flex flex-wrap gap-2">
                    {notCheckedIn.map(student => (
                      <div
                        key={student.userId}
                        className="flex items-center gap-1 px-2 py-1 bg-gray-100 rounded text-xs"
                        title={`${student.firstName} ${student.lastName}`}
                      >
                        <img
                          src={resolveMediaUrl(student.profileImage)}
                          alt={student.firstName}
                          className="w-5 h-5 rounded-full object-cover"
                        />
                        <span>{student.firstName}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="p-6 border-t border-gray-200 flex justify-between items-center">
          <div className="text-sm">
            {checkedInStudents.length === students.length ? (
              <span className="text-green-600 font-semibold flex items-center gap-2">
                <CheckCircle size={18} />
                All students checked in!
              </span>
            ) : (
              <span className={theme.textSecondary}>
                {notCheckedIn.length} student{notCheckedIn.length !== 1 ? 's' : ''} not checked in yet
              </span>
            )}
          </div>
          <div className="flex gap-3">
            <button
              onClick={onClose}
              className={`px-4 py-2 border ${theme.border} rounded-lg hover:bg-gray-50 transition-colors`}
            >
              Cancel
            </button>
            <button
              onClick={handleSaveAndClose}
              className={`px-4 py-2 ${theme.buttonPrimary} rounded-lg transition-colors flex items-center gap-2`}
            >
              <CheckCircle size={18} />
              Save Attendance ({checkedInStudents.length})
            </button>
          </div>
        </div>
      </div>

      <style>{`
        @keyframes fade-in {
          from {
            opacity: 0;
            transform: translateY(-10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        .animate-fade-in {
          animation: fade-in 0.3s ease-out forwards;
        }
      `}</style>
    </div>
  );
};

export default QRAttendanceModal;
